import { Container, MeshRope, Point as PixiPoint, Rectangle, Texture, type DestroyOptions } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { POTS, type PotKind } from "../rules/symbols";
import { FLYING_LIGHT_TUNING } from "../tuning/featureTuning";
import { lightPath, type Point } from "./flightPath";
import { FX_SCOPE } from "./fxScope";
import { FLYING_LIGHT_LAYOUT } from "./layout";

/**
 * The artist's flying light (`flying_light.riv`, played as its Spine conversion): a comet on a loop of its own (its
 * flicker, its sparks), its colour the view model's `color` — a number per pot.
 */
export const FLYING_LIGHT_RIVE = {
  color: "color",
  colors: { life: 1, multi: 2, grow: 3 } satisfies Record<PotKind, number>,
} as const;

/** What the lights ask of their art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface LightArt {
  readonly texture: Texture;
  resize(scale: number): void;
  setNumber(name: string, value: number): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

/** The points the comet's texture is laid along: its columns spread evenly over them, along its length on the path. */
const ROPE_POINTS = 41;

/**
 * The pots' flying lights (the user, 2026-10-10): the artist's comet in a pot's colour, flying head first — up into a
 * pot over its top and down into it, never so tight a turn that the comet folds — at a speed the eye reads as even:
 * the tuning's, eased round a tight turn by the two-thirds power law (`ArcPath.timing`), every flight alike, so its
 * time is its path's. It fades in out of where it starts and out into where it ends, and is bent along its path: the
 * comet's texture is laid on a rope down the arch behind its head (and on past its end, along its line), so its tail
 * follows the curve its head has flown. It sets out as its head alone: its tail grows out of where it started as it
 * flies, never reaching back past it: the whole tail, drawn shorter until the head has flown its length, its faded tip
 * on where it started (cut there instead, its edge showed across a coin: the user, 2026-10-15). One artboard per
 * colour (the file loaded once), each drawn on the UI's runner while a light of its colour flies, at the tuning's frame
 * rate: every light of a colour shows the same frame. The flight is the round's (its scope: a slam ends it); the
 * comet's own loop the UI's, never sped up.
 */
export class FlyingLights extends Container {
  private readonly runner: PresentationTaskRunner;
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private arts: Readonly<Record<PotKind, LightArt>> | null = null;
  private readonly flying: Record<PotKind, number> = { life: 0, multi: 0, grow: 0 };
  private readonly drawing: Record<PotKind, boolean> = { life: false, multi: false, grow: false };
  /** Device pixels per design unit where the lights fly (their canvases' size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { runner: PresentationTaskRunner; ambientRunner: PresentationTaskRunner; log: Log }) {
    super({ label: "FlyingLights" });
    this.runner = input.runner;
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's lights, loaded: an artboard per pot's colour. */
  public setArt(arts: Readonly<Record<PotKind, LightArt>>): void {
    if (this.destroyed || this.arts) {
      for (const kind of POTS) arts[kind].dispose();
      return;
    }
    this.arts = arts;
    for (const kind of POTS) {
      arts[kind].setNumber(FLYING_LIGHT_RIVE.color, FLYING_LIGHT_RIVE.colors[kind]);
      arts[kind].resize(this.canvasScale());
    }
    this.log("[FlyingLights] the lights are in");
  }

  /** Whether the lights are in (until then a flight is the sprite it was). */
  public get ready(): boolean {
    return this.arts !== null;
  }

  /** How many lights of each colour fly now (the dev hook's read). */
  public get inFlight(): Readonly<Record<PotKind, number>> {
    return { ...this.flying };
  }

  /** Device pixels per design unit where the lights fly: their canvases are drawn at their size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    if (this.arts) for (const kind of POTS) this.arts[kind].resize(this.canvasScale());
  }

  /**
   * A light of `pot`'s colour flying from `from` to `to` (`lightPath`: into a pot above it, it lands from above;
   * anywhere else its line is bowed `lift` to its upper side), head first and bent along its path, at the tuning's pace
   * (its time its path's): it fades in out of `from`, its tail growing out of it, and out quickly as its head reaches
   * `to`, and resolves there. `beforeArrival` runs once, its seconds before the head reaches `to` (an impact leading the light
   * in). A `rider` (a coin's amount on its way to the bonus's total) rides its head upright, over it, and is the caller's
   * again once it arrives; `speed` (design units a second) replaces the tuning's. Nothing flies before the lights are in.
   */
  public async fly(input: { pot: PotKind; from: Point; to: Point; lift: number; beforeArrival?: { seconds: number; run: () => void }; rider?: Container; speed?: number }): Promise<void> {
    let arrived = false;
    const arrive = (): void => {
      if (arrived) return;
      arrived = true;
      input.beforeArrival?.run();
    };
    const art = this.arts?.[input.pot];
    if (!art || this.destroyed) {
      arrive();
      return;
    }
    const { artboard, head } = FLYING_LIGHT_LAYOUT;
    const { appearShare, fadeSeconds, minTurnRadius, dropLength, riseBow, speed, fullSpeedRadius, turnSmoothing } = FLYING_LIGHT_TUNING;
    const { units } = this;
    // The comet's length along the path (design units) and its head's place in it (the texture's column).
    const length = artboard.width * units;
    const headAt = head.x / artboard.width;
    const path = lightPath(input.from, input.to, input.lift, { radius: minTurnRadius, drop: dropLength, bow: riseBow });
    const timing = path.timing({ speed: input.speed ?? speed, fullSpeedRadius, smoothing: turnSmoothing });
    const points = Array.from({ length: ROPE_POINTS }, () => new PixiPoint());
    // The comet's texture, whole (its columns over the artboard's, as tall).
    const shown = new Texture({ source: art.texture.source, frame: art.texture.frame.clone(), orig: new Rectangle(0, 0, artboard.width, artboard.height), dynamic: true });
    const rope = new MeshRope({ texture: shown, points });
    // At the head, in the artboard's pixels at the comet's size on the screen.
    const light = new Container({ label: `Light:${input.pot}` });
    light.addChild(rope);
    light.scale.set(units, units);
    light.alpha = 0;
    this.addChild(light);
    const { rider } = input;
    if (rider) {
      rider.position.copyFrom(input.from);
      this.addChild(rider);
    }
    this.flying[input.pot] += 1;
    this.draw(input.pot, art);
    const lead = input.beforeArrival ? Math.min(1, input.beforeArrival.seconds / Math.max(1e-6, timing.seconds)) : 0;
    try {
      await this.runner.tweenProgress(timing.seconds, (k) => {
        if (light.destroyed) return;
        if (k >= 1 - lead) arrive();
        // Where its pace has the head at this share of its time.
        const at = timing.distanceAt(k);
        const centre = path.pointAt(at);
        light.position.set(centre.x, centre.y);
        if (rider && !rider.destroyed) rider.position.set(centre.x, centre.y);
        light.alpha = Math.max(0, Math.min(1, k / appearShare, ((1 - k) * timing.seconds) / fadeSeconds));
        // Its tail grows out of where it set out: the whole tail laid between there and the head until the head has
        // flown its length. (A canvas resized meanwhile is followed.)
        const tail = Math.min(at, headAt * length);
        shown.frame.copyFrom(art.texture.frame);
        shown.update();
        // Each column of the comet where it lies along the path, the head on the flight's point.
        points.forEach((point, i) => {
          const column = i / (ROPE_POINTS - 1);
          const behind = column < headAt ? ((headAt - column) / headAt) * tail : (headAt - column) * length;
          const along = path.pointAt(at - behind);
          point.set((along.x - centre.x) / units, (along.y - centre.y) / units);
        });
      }, FX_SCOPE, { easing: "linear" });
    } finally {
      // A slam's end, or a flight cut short, still arrives.
      arrive();
      if (rider && !rider.destroyed) {
        rider.position.copyFrom(input.to);
        rider.removeFromParent();
      }
      light.destroy({ children: true });
      shown.destroy();
      this.flying[input.pot] -= 1;
    }
  }

  public override destroy(options?: DestroyOptions): void {
    const arts = this.arts;
    this.arts = null;
    if (arts) for (const kind of POTS) arts[kind].dispose();
    super.destroy(options);
  }

  /** Design units per artboard pixel: the comet's head as the tuning's across. */
  private get units(): number {
    return FLYING_LIGHT_TUNING.headSize / FLYING_LIGHT_LAYOUT.headPixels;
  }

  /** Canvas pixels per artboard pixel: the comet's size on the screen. */
  private canvasScale(): number {
    return this.units * this.pixelsPerUnit;
  }

  /** A light of `pot`'s colour is out: its artboard drawn now, then at its own pace while one of its colour flies. */
  private draw(pot: PotKind, art: LightArt): void {
    art.render(0);
    if (this.drawing[pot]) return;
    this.drawing[pot] = true;
    let last = 0;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          art.advance(elapsed - last);
          last = elapsed;
        },
        () => this.destroyed || this.arts?.[pot] !== art || this.flying[pot] === 0,
      )
      .then(() => {
        this.drawing[pot] = false;
        // Another of its colour set out as the last arrived.
        if (!this.destroyed && this.arts?.[pot] === art && this.flying[pot] > 0) this.draw(pot, art);
      });
  }
}
